import { useMemo } from 'react';
import {
	BigNum,
	getPerpFeeTierIndex,
	getUser30dRollingVolumeEstimate,
	PERP_FEE_TIER_MAX_INDEX,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import useRootAuthorityUserStatsAccount from 'src/hooks/query/authority/useRootAuthorityUserStatsAccount';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useAccountExists from 'src/hooks/useAccountExists';

/** No tier to show: no account, or its volume is not loaded yet. */
const NO_TIER = -1;

/**
 * The user's fee tier, split into the tier their volume has earned and the tier
 * they are actually charged.
 *
 * The two differ while a promo is running: `State.promoFeeTier` floors every
 * account at that tier, so an account can be charged above the rung its volume
 * reaches. Keeping them apart lets the modal show real progress up the ladder
 * while still highlighting the rates being charged.
 *
 * Both come from the SDK's `getPerpFeeTierIndex`, which is the selection the
 * program itself makes, so the ladder is not mirrored here.
 */
export default function useUserFeetier() {
	const velocityClient = useVelocityClient();
	const accountExists = useAccountExists();
	const { data: userStatsAccount, isLoading: isLoadingUserStatsAccount } =
		useRootAuthorityUserStatsAccount();

	const state = velocityClient?.getStateAccount();

	// The floor every account is charged at while a promo runs; 0 disables it.
	// Clamped the way the program clamps it. Read independently of whether this
	// visitor has an account, since the promo is a property of the exchange and
	// is worth disclosing either way.
	const promoTierIndex = Math.min(
		state?.promoFeeTier ?? 0,
		PERP_FEE_TIER_MAX_INDEX
	);
	// State arrives on subscribe, which can land either side of the stats.
	const isStateReadable = !!state;

	const { userVolumeTierIndex, volumeAndPromoTierIndex, userCurrent30dVolume } =
		useMemo(() => {
			if (!userStatsAccount || !state) {
				return {
					userVolumeTierIndex: NO_TIER,
					volumeAndPromoTierIndex: NO_TIER,
					userCurrent30dVolume: BigNum.zero(),
				};
			}

			return {
				// The earned tier is the same ladder with the floor turned off,
				// which is what a `promoFeeTier` of 0 means to the program.
				userVolumeTierIndex: getPerpFeeTierIndex(userStatsAccount, {
					...state,
					promoFeeTier: 0,
				}),
				volumeAndPromoTierIndex: getPerpFeeTierIndex(userStatsAccount, state),
				userCurrent30dVolume: BigNum.from(
					getUser30dRollingVolumeEstimate(userStatsAccount),
					QUOTE_PRECISION_EXP
				),
			};
			// `getStateAccount` hands back a fresh object whenever the account
			// updates, so memoise on the one field read off it plus whether it is
			// readable at all, not on its identity.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [userStatsAccount, promoTierIndex, isStateReadable]);

	const isVolumeKnown = userVolumeTierIndex !== NO_TIER;

	// The promo is a floor, not the answer: without the volume tier it only
	// pins the charged tier when it is already the top rung, since nothing can
	// sit above that. Below the top rung an unknown volume could put the user
	// anywhere above the floor, so claim no tier rather than a possibly-lower
	// one. `accountExists` is false both for a visitor with no account and
	// briefly while the lookup is in flight, and showing no tier is right in
	// both cases.
	const userFeeTierIndex = !accountExists
		? NO_TIER
		: isVolumeKnown
			? volumeAndPromoTierIndex
			: promoTierIndex === PERP_FEE_TIER_MAX_INDEX
				? promoTierIndex
				: NO_TIER;

	return {
		/** The rung the user's 30D volume earns. `-1` when there is none to show. */
		userVolumeTierIndex,
		/** The rung the user is charged at: their volume tier, or the promo floor if it is higher. */
		userFeeTierIndex,
		/** Tier every account is floored at while a promo runs; 0 when none is. */
		promoTierIndex,
		/** True while the promo is charging the user better rates than their volume earns. */
		isPromoLiftingUserTier:
			isVolumeKnown && userFeeTierIndex > userVolumeTierIndex,
		userCurrent30dVolume,
		isLoadingVolume: isLoadingUserStatsAccount,
	};
}
